import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import axios from "axios";

function RecruitmentSourceContactsForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();

  const [sources, setSources] = useState([]);
  const [formData, setFormData] = useState({
    organization_recruitment_source_id: "",
    contact_person_name: "",
    designation: "",
    phone_number: "",
    alternate_phone_number: "",
    email: "",
    is_primary: false,
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    const loadSources = async () => {
      if (!org?.organization_id) return;
      try {
        const res = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source?active_only=1`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setSources(res.data?.recruitment_sources || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load recruitment sources");
      }
    };
    loadSources();
  }, [org?.organization_id]);

  useEffect(() => {
    const loadById = async () => {
      try {
        const response = await axios.get(
          `${MAIN_URL}/api/organizations/${org.organization_id}/recruitment-source-contact/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const a = response?.data?.recruitment_source_contact || {};
        setFormData({
          organization_recruitment_source_id:
            a.organization_recruitment_source_id || "",
          contact_person_name: a.contact_person_name || "",
          designation: a.designation || "",
          phone_number: a.phone_number || "",
          alternate_phone_number: a.alternate_phone_number || "",
          email: a.email || "",
          is_primary: !!a.is_primary,
          is_active: a.is_active == null ? true : !!a.is_active,
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load contact");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id && org?.organization_id) {
      setLoading(true);
      loadById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.organization_recruitment_source_id) {
      errors.organization_recruitment_source_id = "Recruitment source is required.";
    }
    if (!formData.contact_person_name?.trim()) {
      errors.contact_person_name = "Contact person name is required.";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);

    const payload = {
      organization_recruitment_source_id:
        formData.organization_recruitment_source_id,
      contact_person_name: formData.contact_person_name,
      designation: formData.designation || null,
      phone_number: formData.phone_number || null,
      alternate_phone_number: formData.alternate_phone_number || null,
      email: formData.email || null,
      is_primary: !!formData.is_primary,
      is_active: !!formData.is_active,
    };

    try {
      if (mode === "edit") {
        await axios.put(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-source-contact/${id}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/recruitment-source-contact`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      toast.success(mode === "edit" ? "Contact Updated!" : "Contact Created!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        toast.error(
          Object.values(validationErrors)
            .map((arr) => (Array.isArray(arr) ? arr[0] : arr))
            .join(" ") || "Validation failed."
        );
      } else {
        toast.error("Something went wrong.");
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Recruitment Source Contact"}
      addMessage={"Recruitment Source Contact"}
      homeLink={"/recruitment/source-contacts"}
      homeText={"Recruitment Source Contacts"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={10}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Recruitment Source"
                    name="organization_recruitment_source_id"
                    disabled={mode === "view"}
                    value={formData.organization_recruitment_source_id}
                    onChange={handleChange}
                    error={!!formErrors.organization_recruitment_source_id}
                    helperText={
                      formErrors.organization_recruitment_source_id ||
                      (sources.length === 0
                        ? "Create a recruitment source first."
                        : "")
                    }
                    required
                  >
                    {sources.map((s) => (
                      <MenuItem
                        key={s.organization_recruitment_source_id}
                        value={s.organization_recruitment_source_id}
                      >
                        {s.recruitment_source_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    name="contact_person_name"
                    disabled={mode === "view"}
                    value={formData.contact_person_name}
                    onChange={handleChange}
                    error={!!formErrors.contact_person_name}
                    helperText={formErrors.contact_person_name}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Designation"
                    name="designation"
                    disabled={mode === "view"}
                    value={formData.designation}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    disabled={mode === "view"}
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone_number"
                    disabled={mode === "view"}
                    value={formData.phone_number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Alternate Phone"
                    name="alternate_phone_number"
                    disabled={mode === "view"}
                    value={formData.alternate_phone_number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_primary}
                        disabled={mode === "view"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_primary: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Primary Contact"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formData.is_active}
                        disabled={mode === "view"}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{ minWidth: 120, textTransform: "capitalize" }}
                  >
                    {btnLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default RecruitmentSourceContactsForm;
